import { startApp } from "emukit";

import { GameboyEmulator } from "./ts";

const BACKGROUNDS = [
    "264653",
    "1b1a17",
    "023047",
    "bc6c25",
    "283618",
    "2a9d8f",
    "3a5a40"
];

const ONLINE_ROMS = [
    { name: "Pokemon Red (RB)", url: "/static/roms/rb-pokered.gbc" },
    { name: "Pokemon Blue (RB)", url: "/static/roms/rb-pokeblue.gbc" },
    { name: "Pokemon Red (RGB)", url: "/static/roms/rgb-pokered.gbc" },
    { name: "Pokemon Blue (RGB)", url: "/static/roms/rgb-pokeblue.gbc" },
    { name: "Pokemon Green (RGB)", url: "/static/roms/rgb-pokegreen.gbc" },
    { name: "Pokemon Yellow (US)", url: "/static/roms/US.pokeyellow.gbc" },
    { name: "Pokemon Yellow (JP)", url: "/static/roms/SJP.pokeyellow.gbc" }
];

(async () => {
    // tries to load the settings from the local storage
    // to be used as fallback process for GET parameters
    let settings: Record<string, string> = {};
    if (window.localStorage) {
        settings = JSON.parse(localStorage.getItem("settings") ?? "{}");
    }

    // parses the current location URL as retrieves
    // some of the "relevant" GET parameters for logic
    const params = new URLSearchParams(window.location.search);
    const romUrl = params.get("rom_url") ?? params.get("url") ?? undefined;
    const fullscreen = ["1", "true", "True"].includes(
        params.get("fullscreen") ?? params.get("fs") ?? ""
    );
    const debug = ["1", "true", "True"].includes(params.get("debug") ?? "");
    const verbose = ["1", "true", "True"].includes(params.get("verbose") ?? "");
    const keyboard = ["1", "true", "True"].includes(
        params.get("keyboard") ?? ""
    );
    const hideInfo = ["1", "true", "True"].includes(
        params.get("hide_info") ?? "1"
    ); // default to true (hide)
    const palette = params.get("palette") ?? settings["palette"] ?? undefined;
    const background =
        params.get("background") ??
        params.get("theme") ??
        settings["background"] ??
        settings["theme"] ??
        undefined;

    // creates the emulator structure and initializes the
    // React app with both the parameters and the emulator
    const emulator = new GameboyEmulator({
        background: background,
        debug: debug || verbose
    });
    await emulator.init();
    emulator.setRoms(ONLINE_ROMS);
    startApp("app", {
        emulator: emulator,
        fullscreen: fullscreen,
        debug: debug,
        keyboard: keyboard,
        palette: palette,
        background: background,
        backgrounds: BACKGROUNDS
    });

    // sets the emulator in the global scope this is useful
    // to be able to access the emulator from global functions
    window.emulator = emulator;

    // hide/remove unwanted UI elements - IMPORTANT: do this BEFORE emulator.start()
    // because emulator.start() runs an infinite loop and never returns
    // can be controlled with ?hide_info=0 URL parameter to show them
    if (hideInfo) {
        // Use MutationObserver to watch for DOM changes and hide elements
        // when they appear - more reliable than setTimeout
        const hideUIElements = () => {
            let elementsFound = false;

            // Remove the title section (Boytacean 0.11.5 with icon)
            const titleElement = document.querySelector("h1.title");
            if (titleElement) {
                titleElement.remove();
                elementsFound = true;
            }

            // Hide the description section by removing the 'visible' class
            // This section contains the "This is a Game Boy emulator..." text
            const sections = document.querySelectorAll(".section.visible");
            sections.forEach((section) => {
                // Check if this section contains the description paragraph
                const paragraph = section.querySelector("p.paragraph");
                if (
                    paragraph &&
                    paragraph.textContent?.includes("emulator built using the")
                ) {
                    section.classList.remove("visible");
                    elementsFound = true;
                }
            });

            return elementsFound;
        };

        // Try to hide elements immediately
        const foundNow = hideUIElements();

        // If elements weren't found yet, set up a MutationObserver
        if (!foundNow) {
            const observer = new MutationObserver((mutations, obs) => {
                const found = hideUIElements();
                // If we found and hid the elements, stop observing
                if (found) {
                    obs.disconnect();
                }
            });

            // Start observing the document for DOM changes
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });

            // Fallback: stop observing after 5 seconds
            setTimeout(() => {
                observer.disconnect();
            }, 5000);
        }
    }

    // starts the emulator with the provided ROM URL, this is
    // going to run the main emulator (infinite) loop
    await emulator.start({ romUrl: romUrl });
})();
